
import { useRef } from 'react';

interface Event {
  id: number;
  title: string;
  image: string;
  category: string;
  month: string;
  day: string;
  time: string;
  location: string;
}

interface EventDetails {
  fullDescription?: string;
  description?: string;
  address?: string;
  admission?: string;
  ageGroup?: string;
  organizer?: string;
  website?: string;
  phone?: string;
  highlights?: string[];
  parking?: string;
  amenities?: string[];
  price?: string;
}

interface PrintEventFlyerButtonProps {
  event: Event;
  details: EventDetails;
}

export default function PrintEventFlyerButton({ event, details }: PrintEventFlyerButtonProps) {
  const printRef = useRef<HTMLDivElement>(null);

  const handlePrint = () => {
    const description = details.fullDescription || details.description || '';
    const address = details.address || `${event.location}, Roseville, CA`;
    const admission = details.admission || details.price || 'See event website';
    const highlights = details.highlights || [];
    const amenities = details.amenities || [];

    const printContent = `
      <!DOCTYPE html>
      <html lang="en">
      <head>
        <meta charset="UTF-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <title>${event.title} — Event Flyer</title>
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

          * { margin: 0; padding: 0; box-sizing: border-box; }

          body {
            font-family: 'Inter', sans-serif;
            background: #fff;
            color: #111827;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }

          .page {
            width: 8.5in;
            min-height: 11in;
            margin: 0 auto;
            padding: 0;
            position: relative;
          }

          /* Header band */
          .header {
            background: #0d9488;
            color: #fff;
            padding: 28px 40px 24px;
            display: flex;
            align-items: center;
            justify-content: space-between;
          }
          .header-logo {
            display: flex;
            align-items: center;
            gap: 10px;
          }
          .header-logo-icon {
            width: 36px;
            height: 36px;
            background: rgba(255,255,255,0.2);
            border-radius: 8px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 18px;
          }
          .header-logo-text {
            font-size: 18px;
            font-weight: 700;
            letter-spacing: -0.3px;
          }
          .header-category {
            background: rgba(255,255,255,0.2);
            border-radius: 20px;
            padding: 5px 16px;
            font-size: 13px;
            font-weight: 600;
            letter-spacing: 0.5px;
            text-transform: uppercase;
          }

          /* Hero image */
          .hero-image {
            width: 100%;
            height: 280px;
            object-fit: cover;
            object-position: top;
            display: block;
          }

          /* Date badge overlay */
          .hero-wrapper {
            position: relative;
          }
          .date-badge {
            position: absolute;
            top: 20px;
            left: 40px;
            background: #fff;
            border-radius: 12px;
            padding: 10px 18px;
            text-align: center;
            box-shadow: 0 4px 16px rgba(0,0,0,0.18);
          }
          .date-badge-month {
            font-size: 11px;
            font-weight: 700;
            color: #0d9488;
            text-transform: uppercase;
            letter-spacing: 1px;
          }
          .date-badge-day {
            font-size: 32px;
            font-weight: 800;
            color: #111827;
            line-height: 1;
          }

          /* Body */
          .body {
            padding: 32px 40px 24px;
          }

          .event-title {
            font-size: 30px;
            font-weight: 800;
            color: #111827;
            line-height: 1.2;
            margin-bottom: 20px;
          }

          /* Info row */
          .info-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 14px;
            margin-bottom: 24px;
          }
          .info-item {
            display: flex;
            align-items: flex-start;
            gap: 10px;
            background: #f0fdfa;
            border-radius: 10px;
            padding: 12px 14px;
          }
          .info-icon {
            width: 32px;
            height: 32px;
            background: #0d9488;
            border-radius: 8px;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            font-size: 15px;
            color: #fff;
          }
          .info-label {
            font-size: 11px;
            color: #6b7280;
            font-weight: 500;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            margin-bottom: 2px;
          }
          .info-value {
            font-size: 13px;
            font-weight: 600;
            color: #111827;
            line-height: 1.4;
          }

          /* Description */
          .section-title {
            font-size: 15px;
            font-weight: 700;
            color: #111827;
            margin-bottom: 8px;
            padding-bottom: 6px;
            border-bottom: 2px solid #ccfbf1;
          }
          .description {
            font-size: 13px;
            color: #374151;
            line-height: 1.7;
            margin-bottom: 22px;
          }

          /* Highlights */
          .highlights-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 8px;
            margin-bottom: 22px;
          }
          .highlight-item {
            display: flex;
            align-items: center;
            gap: 8px;
            font-size: 12px;
            color: #374151;
          }
          .highlight-dot {
            width: 18px;
            height: 18px;
            background: #0d9488;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            color: #fff;
            font-size: 10px;
          }

          /* Amenities */
          .amenities-row {
            display: flex;
            flex-wrap: wrap;
            gap: 8px;
            margin-bottom: 24px;
          }
          .amenity-chip {
            background: #f3f4f6;
            border-radius: 20px;
            padding: 4px 12px;
            font-size: 11px;
            font-weight: 500;
            color: #374151;
          }

          /* CTA footer */
          .cta-footer {
            background: #f0fdfa;
            border-top: 2px solid #99f6e4;
            padding: 18px 40px;
            display: flex;
            align-items: center;
            justify-content: space-between;
          }
          .cta-left {
            font-size: 12px;
            color: #6b7280;
          }
          .cta-left strong {
            display: block;
            font-size: 14px;
            color: #0d9488;
            font-weight: 700;
            margin-bottom: 2px;
          }
          .cta-website {
            font-size: 13px;
            color: #0d9488;
            font-weight: 600;
            text-decoration: none;
          }
          .qr-placeholder {
            width: 64px;
            height: 64px;
            border: 2px dashed #0d9488;
            border-radius: 8px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 9px;
            color: #0d9488;
            text-align: center;
            font-weight: 600;
            line-height: 1.3;
          }

          /* Print rules */
          @media print {
            body { margin: 0; }
            .page { width: 100%; }
            @page { margin: 0; size: letter; }
          }
        </style>
      </head>
      <body>
        <div class="page">
          <!-- Header -->
          <div class="header">
            <div class="header-logo">
              <div class="header-logo-icon">🏘</div>
              <span class="header-logo-text">Roseville.org</span>
            </div>
            <span class="header-category">${event.category}</span>
          </div>

          <!-- Hero Image -->
          <div class="hero-wrapper">
            <img class="hero-image" src="${event.image}" alt="${event.title}" crossorigin="anonymous" />
            <div class="date-badge">
              <div class="date-badge-month">${event.month}</div>
              <div class="date-badge-day">${event.day}</div>
            </div>
          </div>

          <!-- Body -->
          <div class="body">
            <h1 class="event-title">${event.title}</h1>

            <!-- Info Grid -->
            <div class="info-grid">
              <div class="info-item">
                <div class="info-icon">📅</div>
                <div>
                  <div class="info-label">Date &amp; Time</div>
                  <div class="info-value">${event.month} ${event.day} &bull; ${event.time}</div>
                </div>
              </div>
              <div class="info-item">
                <div class="info-icon">📍</div>
                <div>
                  <div class="info-label">Location</div>
                  <div class="info-value">${event.location}<br/><span style="font-weight:400;color:#6b7280;font-size:12px">${address}</span></div>
                </div>
              </div>
              <div class="info-item">
                <div class="info-icon">🎟</div>
                <div>
                  <div class="info-label">Admission</div>
                  <div class="info-value">${admission}</div>
                </div>
              </div>
              <div class="info-item">
                <div class="info-icon">👥</div>
                <div>
                  <div class="info-label">Organizer</div>
                  <div class="info-value">${details.organizer || 'Roseville.org'}</div>
                </div>
              </div>
            </div>

            <!-- Description -->
            ${description ? `
            <div class="section-title">About This Event</div>
            <p class="description">${description.replace(/\n/g, '<br/>')}</p>
            ` : ''}

            <!-- Highlights -->
            ${highlights.length > 0 ? `
            <div class="section-title">Event Highlights</div>
            <div class="highlights-grid">
              ${highlights.map((h: string) => `
                <div class="highlight-item">
                  <div class="highlight-dot">✓</div>
                  <span>${h}</span>
                </div>
              `).join('')}
            </div>
            ` : ''}

            <!-- Amenities -->
            ${amenities.length > 0 ? `
            <div class="section-title">Amenities &amp; Features</div>
            <div class="amenities-row">
              ${amenities.map((a: string) => `<span class="amenity-chip">${a}</span>`).join('')}
            </div>
            ` : ''}
          </div>

          <!-- CTA Footer -->
          <div class="cta-footer">
            <div class="cta-left">
              <strong>More events at Roseville.org</strong>
              Discover what&apos;s happening in your community
            </div>
            ${details.website ? `<a class="cta-website" href="${details.website}">${details.website.replace('https://', '')}</a>` : ''}
            <div class="qr-placeholder">Scan for<br/>details</div>
          </div>
        </div>

        <script>
          window.onload = function() { window.print(); };
        </script>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank', 'width=900,height=700');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
    }
  };

  return (
    <button
      onClick={handlePrint}
      className="w-full flex items-center justify-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-800 font-semibold py-3 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
      title="Print event flyer"
    >
      <div className="w-5 h-5 flex items-center justify-center">
        <i className="ri-printer-line text-gray-600"></i>
      </div>
      <span>Print Event Flyer</span>
    </button>
  );
}
